import { Injectable } from '@angular/core';
import { AppConfig, UvExplorerClient } from 'common';
import { Observable, Subject } from 'rxjs/Rx';
import {
	NetViewTreeDto, NetViewDto, PrepareNetworkTransferResultDto, NetworkTransferResultDto
} from 'UvExplorerServer.Dtos.TS/NetViews';
import { AgentDto } from 'UvExplorerServer.Dtos.TS/Agents';
import { ValueEncoder } from "DiscoveryEngine.TS/Utils";
import { UpdateResultDto } from "UvExplorerServer.Dtos.TS";
import { ScheduledDiscoveryStatusDto } from "UvExplorerServer.Dtos.TS/Discovery";
import { ScheduledDiscovery, DiscoverySettings } from "DiscoveryEngine.TS/Discovery";
import { DiscoveryTaskHandle } from "DiscoveryEngine.TS/DiscoveryService";
import { DiscoverySubtaskHandle } from "DiscoveryEngine.TS/Discovery";


@Injectable()
export class NetViewWebService {
	constructor(private appConfig: AppConfig, private uvExplorerClient: UvExplorerClient) {
		return;
	}

	public getNetViewTree(dataSelector: string): Observable<NetViewTreeDto> {
		let url = this.appConfig.dataServiceUrl + `api/data/netviewtree/${dataSelector}`;
		return this.uvExplorerClient.getObjSer<NetViewTreeDto>(url, NetViewTreeDto);
	}

	//
	// NetViews
	//

	public getNetViewByGuid(guid: string): Observable<NetViewDto> {
		let url = this.appConfig.configServiceUrl + `api/config/netviews/guid/${guid}`;
		return this.uvExplorerClient.getObjSer<NetViewDto>(url, NetViewDto);
	}

	public createNetView(netView: NetViewDto, parentGuid: string, position: number,
		isScope: boolean): Observable<NetViewDto> {

		let body = {
			create_net_view_request: {
				parent_guid: parentGuid,
				position: ValueEncoder.EncodeInteger(position),
				is_scope: ValueEncoder.EncodeBoolean(isScope)
			}
		};

		netView.Write(body.create_net_view_request, {});

		let url = this.appConfig.configServiceUrl + `api/config/netviews`;
		let subject = new Subject<NetViewDto>();

		this.uvExplorerClient.postObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				netView.CopyProps(result, true);
				subject.next(netView);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateNetView(netView: NetViewDto): Observable<NetViewDto> {
		let body = {};
		netView.Write(body, {});

		let url = this.appConfig.configServiceUrl + `api/config/netviews`;
		let subject = new Subject<NetViewDto>();

		this.uvExplorerClient.putObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				netView.CopyProps(result, false);
				subject.next(netView);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteNetViewByGuid(guid: string, parentGuid: string): Observable<boolean> {
		let body = {
			delete_net_view_by_guid_request: {
				net_view_guid: guid,
				parent_guid: parentGuid
			}
		};

		let url = this.appConfig.configServiceUrl + `api/config/netviews/guid`;
		return this.uvExplorerClient.deleteNoResult(url, body);
	}

	public prepareNetworkTransfer(scopeGuid: string, oldUserGuid: string, newUserGuid: string):
		Observable<PrepareNetworkTransferResultDto> {
		let body = {
			prepare_network_transfer_request: {
				user_guid: oldUserGuid,
				scope_guid: scopeGuid,
				target_user_guid: newUserGuid,
			}
		};

		let url = this.appConfig.configServiceUrl + `api/config/netviews/preparetransfer`;
		return this.uvExplorerClient.postObjSer(url, body, PrepareNetworkTransferResultDto);
	}

	public doNetworkTransfer(scopeGuid: string, oldUserGuid: string, newUserGuid: string,
		apiKeyGuids: string[]): Observable<NetworkTransferResultDto> {

		let apiKeyGuidsValues = [];
		apiKeyGuids.forEach(v => {
			apiKeyGuidsValues.push({ value: v });
		}, this);

		let body = {
			network_transfer_request: {
				user_guid: oldUserGuid,
				scope_guid: scopeGuid,
				target_user_guid: newUserGuid,
				api_key_guids: apiKeyGuidsValues,
			}
		};

		let url = this.appConfig.configServiceUrl + `api/config/netviews/transfer`;
		return this.uvExplorerClient.postObjSer(url, body, NetworkTransferResultDto);
	}

	//
	// Agents
	//

	public getAgentByGuid(agentGuid: string): Observable<AgentDto> {
		let url = this.appConfig.configServiceUrl + `api/config/agents/guid/${agentGuid}`;
		return this.uvExplorerClient.getObjSer<AgentDto>(url, AgentDto);
	}

	public unbindAgent(agent: AgentDto): Observable<boolean> {
		let body = {
			unbind_agent_request: {
				agent_guid: agent.Guid,
				client_agent_guid: agent.ClientAgentGuid
			}
		};

		let url = this.appConfig.configServiceUrl + `api/config/agents/unbind`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public createAgent(agent: AgentDto, parentGuid: string, position: number): Observable<AgentDto> {
		let body = {
			create_agent_request: {
				parent_guid: parentGuid,
				position: ValueEncoder.EncodeInteger(position)
			}
		};

		agent.Write(body.create_agent_request, {});

		let url = this.appConfig.configServiceUrl + `api/config/agents`;
		let subject = new Subject<AgentDto>();

		this.uvExplorerClient.postObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				agent.CopyProps(result, true);
				subject.next(agent);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public updateAgent(agent: AgentDto): Observable<AgentDto> {
		let body = {
			update_agent_request: {
			}
		};

		agent.Write(body.update_agent_request, {});

		let url = this.appConfig.configServiceUrl + `api/config/agents`;
		let subject = new Subject<AgentDto>();

		this.uvExplorerClient.putObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				agent.CopyProps(result, false);
				subject.next(agent);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteAgentByGuid(guid: string, parentGuid: string): Observable<boolean> {
		let body = {
			delete_agent_by_guid_request: {
				agent_guid: guid,
				parent_guid: parentGuid
			}
		};

		let url = this.appConfig.configServiceUrl + `api/config/agents/guid`;
		return this.uvExplorerClient.deleteNoResult(url, body);
	}

	public dumpAgentConfig(guid: string): Observable<boolean> {
		let url = this.appConfig.agentServiceUrl + `api/agents/dumpagentconfig/${guid}`;
		return this.uvExplorerClient.postNoResult(url, "");
	}

	public runDiscoveryAsap(agent: AgentDto, schedDiscGuid: string): Observable<any> {
		let url = this.appConfig.configServiceUrl + `api/config/agents/runasap/${agent.Guid}/${schedDiscGuid}`;
		let subject = new Subject<any>();

		this.uvExplorerClient.postNoResult(url, "").subscribe(
			(result: any) => {
				subject.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public cancelDiscoveryAsap(agent: AgentDto, discTaskHandle: DiscoveryTaskHandle): Observable<any> {
		let body = {
			cancel_discovery_request: {
				agent_guid: agent.Guid,
			}
		};
		if (discTaskHandle) {
			discTaskHandle.Write(body.cancel_discovery_request, {});
		}

		let url = this.appConfig.configServiceUrl + `api/config/agents/cancelasap`;
		let subject = new Subject<any>();

		this.uvExplorerClient.postNoResult(url, body).subscribe(
			(result: any) => {
				subject.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public cancelSubtaskAsap(agent: AgentDto, discTaskHandle: DiscoveryTaskHandle, subtaskHandle: DiscoverySubtaskHandle): Observable<any> {
		let body = {
			cancel_subtask_request: {
				agent_guid: agent.Guid,
			}
		};
		if (discTaskHandle) {
			discTaskHandle.Write(body.cancel_subtask_request, {});
		}
		if (subtaskHandle) {
			subtaskHandle.Write(body.cancel_subtask_request, {});
		}

		let url = this.appConfig.configServiceUrl + `api/config/agents/cancelsubtaskasap`;
		let subject = new Subject<any>();

		this.uvExplorerClient.postNoResult(url, body).subscribe(
			(result: any) => {
				subject.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public runMonitorAsap(agent: AgentDto, policyGuid: string): Observable<any> {
		let url = this.appConfig.configServiceUrl + `api/config/agents/runmonitorasap/${agent.Guid}/${policyGuid}`;
		let subject = new Subject<any>();

		this.uvExplorerClient.postNoResult(url, "").subscribe(
			(result: any) => {
				subject.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public clearMonitorPolicyHistoryAsap(agent: AgentDto, policyGuid: string): Observable<any> {
		let url = this.appConfig.configServiceUrl + `api/config/agents/clearmonitorpolicyhistoryasap/${agent.Guid}/${policyGuid}`;
		let subject = new Subject<any>();

		this.uvExplorerClient.postNoResult(url, "").subscribe(
			(result: any) => {
				subject.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public clearDeviceMonitorHistoryAsap(agent: AgentDto, deviceGuid: string): Observable<any> {
		let url = this.appConfig.configServiceUrl + `api/config/agents/cleardevicemonitorhistoryasap/${agent.Guid}/${deviceGuid}`;
		let subject = new Subject<any>();

		this.uvExplorerClient.postNoResult(url, "").subscribe(
			(result: any) => {
				subject.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public getSchedDiscStatus(schedDiscGuid: string): Observable<ScheduledDiscoveryStatusDto> {
		let url = this.appConfig.dataServiceUrl + `api/data/scheddiscstatus/guid/${schedDiscGuid}`;
		return this.uvExplorerClient.getObjSer<ScheduledDiscoveryStatusDto>(url, ScheduledDiscoveryStatusDto);
	}

	public createScheduledDiscovery(agent: AgentDto, schedDisc: ScheduledDiscovery,
		discSettings: DiscoverySettings): Observable<AgentDto> {

		let url = this.appConfig.configServiceUrl + `api/config/scheddiscs`;
		let subject = new Subject<AgentDto>();

		let body = {
			create_sched_disc_request: {
				parent_guid: agent.Guid
			}
		};

		schedDisc.Write(body.create_sched_disc_request, {});
		discSettings.Write(body.create_sched_disc_request, {});

		this.uvExplorerClient.postObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				agent.DiscoverySettings.Add(discSettings);
				agent.ScheduledDiscoveries.Add(schedDisc);

				agent.CopyProps(result, false);

				subject.next(agent);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public updateScheduledDiscovery(agent: AgentDto, schedDisc: ScheduledDiscovery,
		discSettings: DiscoverySettings): Observable<AgentDto> {

		let url = this.appConfig.configServiceUrl + `api/config/scheddiscs`;
		let subject = new Subject<AgentDto>();

		let body = {
			update_sched_disc_request: {
				parent_guid: agent.Guid
			}
		};

		schedDisc.Write(body.update_sched_disc_request, {});
		discSettings.Write(body.update_sched_disc_request, {});

		this.uvExplorerClient.putObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				for (let i = 0; i < agent.DiscoverySettings.Count; ++i) {
					if (agent.DiscoverySettings.Get(i).GUID == discSettings.GUID) {
						agent.DiscoverySettings.Set(i, discSettings);
						break;
					}
				}
				for (let i = 0; i < agent.ScheduledDiscoveries.Count; ++i) {
					if (agent.ScheduledDiscoveries.Get(i).GUID == schedDisc.GUID) {
						agent.ScheduledDiscoveries.Set(i, schedDisc);
						break;
					}
				}

				agent.CopyProps(result, false);

				subject.next(agent);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public deleteScheduledDiscovery(agent: AgentDto, schedDiscGuid: string, discSettingsGuid: string): Observable<AgentDto> {

		let url = this.appConfig.configServiceUrl + `api/config/scheddiscs`;
		let subject = new Subject<AgentDto>();

		let body = {
			delete_sched_disc_request: {
				parent_guid: agent.Guid,
				sched_disc_guid: schedDiscGuid,
				disc_settings_guid: discSettingsGuid
			}
		};

		this.uvExplorerClient.deleteObjSer<UpdateResultDto>(url, body, UpdateResultDto).subscribe(
			(result: UpdateResultDto) => {
				for (let i = 0; i < agent.DiscoverySettings.Count; ++i) {
					if (agent.DiscoverySettings.Get(i).GUID == discSettingsGuid) {
						agent.DiscoverySettings.RemoveAt(i);
						break;
					}
				}
				for (let i = 0; i < agent.ScheduledDiscoveries.Count; ++i) {
					if (agent.ScheduledDiscoveries.Get(i).GUID == schedDiscGuid) {
						agent.ScheduledDiscoveries.RemoveAt(i);
						break;
					}
				}

				agent.CopyProps(result, false);

				subject.next(agent);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

}